import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { TestTube, Spinner, CheckCircle, XCircle } from "@phosphor-icons/react";

/**
 * `TestSequenceButton` — drop-in CTA on any workflow surface to fire
 * every email step at the marketer's own inbox so they can preview the
 * whole sequence before enroling real prospects.
 *
 * Safety: the backend endpoint is the gate — it never touches real
 * mkt_workflow_enrollments / crm_lead_email_log rows. This component is
 * purely a thin wrapper around that endpoint.
 *
 * Props:
 *   kind        Always 'workflow' (kept for backward-compat callers)
 *   id          The workflow_id
 *   sequenceName Optional — shown in the modal so the user knows what
 *               they're testing. Falls back to the id if omitted.
 *   variant     Button visual variant — defaults to 'outline'
 *   size        Button size — defaults to 'sm'
 *   className   Additional classes applied to the trigger button
 */
export default function TestSequenceButton({
  kind = "workflow", id, sequenceName, variant = "outline", size = "sm", className = "",
}) {
  const [open, setOpen] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [firstName, setFirstName] = useState("Test");
  const [lastName, setLastName] = useState("Recipient");
  const [companyName, setCompanyName] = useState("Test Company Inc.");
  const [allVariants, setAllVariants] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const r = await api.post("/marketing/test-sequence", {
        kind, id, to_email: toEmail.trim(),
        first_name: firstName.trim() || "Test",
        last_name: lastName.trim() || "Recipient",
        company_name: companyName.trim() || "Test Company Inc.",
        all_variants: allVariants,
      });
      setResult(r.data);
      if (r.data?.sent === r.data?.total && r.data?.total > 0) {
        toast.success(`Sent ${r.data.sent} test emails to ${toEmail}`);
      } else if (r.data?.total === 0) {
        toast.warning("This sequence has no email steps to test.");
      } else {
        toast.warning(`Partial send — ${r.data.sent} of ${r.data.total} sent.`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Request failed";
      toast.error(`Test send failed: ${msg}`);
      setResult({ ok: false, error: msg });
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setResult(null);
    // keep the email/name fields so the user can quickly re-fire if needed
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          data-testid={`test-sequence-${kind}-${id}`}
        >
          <TestTube size={14} className="mr-1.5" weight="bold" />
          Test sequence
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="test-sequence-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TestTube size={18} weight="bold" />
            Test the workflow sequence
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-[12px] text-[#525252]">
              Sends every email step in <strong>{sequenceName || id}</strong> to a single
              address you choose. No real prospects are touched.
            </p>

            <div className="space-y-1">
              <Label htmlFor="to-email" className="text-[11px] font-bold uppercase tracking-wide">
                Send to
              </Label>
              <Input
                id="to-email"
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="you@yourdomain.com"
                required
                autoFocus
                data-testid="test-sequence-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="first-name" className="text-[11px] font-bold uppercase tracking-wide">
                  First name
                </Label>
                <Input
                  id="first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="test-sequence-first-name"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="last-name" className="text-[11px] font-bold uppercase tracking-wide">
                  Last name
                </Label>
                <Input
                  id="last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  data-testid="test-sequence-last-name"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="company-name" className="text-[11px] font-bold uppercase tracking-wide">
                Company name
              </Label>
              <Input
                id="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                data-testid="test-sequence-company"
              />
            </div>

            <p className="text-[10px] text-[#A3A3A3] leading-tight">
              These values fill in <code>{`{{first_name}}`}</code>,{" "}
              <code>{`{{last_name}}`}</code> and <code>{`{{company}}`}</code> in
              the email templates so you can preview personalisation.
            </p>

            <label className="flex items-start gap-2 p-2 border border-[#E5E5E5] hover:border-[#7C3AED] cursor-pointer"
                   data-testid="test-sequence-all-variants-label">
              <input
                type="checkbox"
                checked={allVariants}
                onChange={(e) => setAllVariants(e.target.checked)}
                className="mt-0.5"
                data-testid="test-sequence-all-variants"
              />
              <span className="text-[11px] leading-tight">
                <span className="font-bold text-[#7C3AED] uppercase tracking-wider text-[10px] block">
                  Include all A/B/C variants
                </span>
                <span className="text-[#525252]">
                  Fires one email per variant on every split-tested step so you can see every
                  variant side-by-side in your inbox. Steps without variants send once as usual.
                </span>
              </span>
            </label>

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !toEmail} data-testid="test-sequence-submit">
                {submitting ? (
                  <><Spinner size={14} className="mr-1.5 animate-spin" /> Sending…</>
                ) : allVariants ? (
                  <>Send all variants</>
                ) : (
                  <>Send all emails</>
                )}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3" data-testid="test-sequence-result">
            <div className={`p-3 border ${result.ok && result.sent === result.total
              ? "border-[#16A34A] bg-[#F0FDF4]" : "border-[#F59E0B] bg-[#FFFBEB]"}`}>
              <div className="flex items-center gap-2 text-[13px] font-semibold">
                {result.ok && result.sent === result.total ? (
                  <><CheckCircle size={16} weight="fill" className="text-[#16A34A]" />
                    Sent {result.sent}/{result.total} test emails to {result.to_email}</>
                ) : result.error ? (
                  <><XCircle size={16} weight="fill" className="text-[#DC2626]" />
                    Failed: {result.error}</>
                ) : (
                  <><XCircle size={16} weight="fill" className="text-[#F59E0B]" />
                    Sent {result.sent}/{result.total}</>
                )}
              </div>
              {result.results && result.results.length > 0 && (
                <ul className="mt-2 space-y-1 text-[11px]">
                  {result.results.map((r) => (
                    <li key={`${r.step_number}-${r.variant_id || "base"}`} className="flex items-start gap-1.5">
                      {r.delivered
                        ? <CheckCircle size={11} weight="fill" className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                        : <XCircle size={11} weight="fill" className="text-[#DC2626] mt-0.5 flex-shrink-0" />}
                      {r.variant_id && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1 bg-[#F5F3FF] text-[#7C3AED] border border-[#E9D5FF] mt-0.5 flex-shrink-0">
                          {r.variant_id}
                        </span>
                      )}
                      <span className="text-[#525252] truncate">{r.subject}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-[11px] text-[#A3A3A3]">
              Check the inbox at <strong>{result.to_email}</strong> in 30-90 seconds. Look in
              spam if the first email doesn't appear.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={reset}>Send another</Button>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
